import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";
import { AgGridTable } from "../../Component/AgGridTable";
import { ColDef, GridApi } from "ag-grid-community";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import {
  fetchCustomers,
  fetchProjects,
  getTimeSheetDetailsById,
  GetProjectByCustomerId,
  fetchGetYears,
  fetchGetMonths,
  CreateTimesheet,
} from "../../services/timesheetService";
import { Timeline } from "@mui/icons-material";
import StickyFooterButtons from "../../Component/StickyFooterButtons";
import { Snackbar, Alert } from "@mui/material";
import ConfirmationDialog from "../../Component/ConfirmationDialog";


type DropdownOption = {
  tableID: number;
  tableName: string;
};

const editColumns: ColDef[] = [

  { field: "employeeCode", headerName: "Employee Code", editable: false },
  { field: "firstName", headerName: "First Name", editable: false },
  { field: "lastName", headerName: "Last Name", editable: false },
  {
    headerName: "Booked Hours",
    field: "bookedHours",
    editable: true,
    type: "numericColumn",
    valueParser: (params: any) => {
      const parsed = parseFloat(params.newValue);
      return isNaN(parsed) ? 0 : parsed;
    },
  },
];

const AddTimesheet: React.FC = () => {
  const [addTableData, setAddTableData] = useState<
    {
      employeeId: any;
      employeeCode: any;
      firstName: string;
      lastName: string;
      bookedHours: number;
    }[]
  >([]);
  const [customers, setCustomers] = useState<
    { customerId: number; customerName: string }[]
  >([]);
  const [projects, setProjects] = useState<
    { projectId: number; projectName: string }[]
  >([]);
  const [years, setYears] = useState<
    { tableID: any; tableValueLong: any }[]
  >([]);
  const [months, setMonths] = useState<
    { tableID: any; tableName: any }[]
  >([]);
  const [customer, setCustomer] = useState<number | "">("");
  const [project, setProject] = useState<number | "">("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [timesheetId, setTimesheetId] = useState<string>("");
  const [EmployeeId, setEmployeeId] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);



  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const gridApi = useRef<GridApi | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const customerData = await fetchCustomers();
        setCustomers(Array.isArray(customerData.data) ? customerData.data : []);

        const yearData = await fetchGetYears();
        const fetchedYears = Array.isArray(yearData.data) ? yearData.data : [];
        setYears(fetchedYears);

        const monthData = await fetchGetMonths();
        const fetchedMonths = Array.isArray(monthData.data)
          ? monthData.data
          : [];
        setMonths(fetchedMonths);

        const currentDate = new Date();
        const currentMonthName = currentDate.toLocaleString("default", {
          month: "short",
        });
        const currentYear = currentDate.getFullYear();

        const matchedMonth = fetchedMonths.find(
          (m: DropdownOption) =>
            m.tableName.toLowerCase() === currentMonthName.toLowerCase()
        );
        const matchedYear = fetchedYears.find(
          (y: DropdownOption) => y.tableName === currentYear.toString()
        );

        if (!month && matchedMonth) {
          setMonth(matchedMonth.tableID);
        }

        if (!year && matchedYear) {
          setYear(matchedYear.tableID);
        }
      } catch (error) {
        console.error("Failed to load filter options:", error);
      }
    };

    loadData();
  }, []);
  const handleReset = () => {
    setCustomer("");
    setProject("");
    setProjects([]);
    setMonth("");
    setYear("");
  };
  const [employeeData, setEmployeeData] = useState<any[]>([]);
  useEffect(() => {
    if (id) {
      loadGetTimesheetDetailsById(+id);
    }
  }, [id]);

  const loadGetTimesheetDetailsById = async (timesheetID: number) => {
    try {
      const response = await getTimeSheetDetailsById(timesheetID);

      const details = response?.data?.timesheetDetailModel ?? [];

      // Summary data for employee section
      const employeeSummary = details.map((emp: any) => ({
        employeeId: emp.employeeId,
        firstName: emp.firstName,
        lastName: emp.lastName,
        bookedHours: emp.bookedHours ?? 0,
      }));

      // Detailed data for editable table
      const formattedTableData = details.map((item: any) => ({
        employeeId: item.employeeId,
        employeeCode: item.employeeCode ?? "",
        firstName: item.firstName,
        lastName: item.lastName,
        bookedHours: item.bookedHours ?? 0,
      }));

      // setEmployeeData(employeeSummary);    
      setAddTableData(formattedTableData);

    } catch (error) {
      console.error("Error loading timesheet details:", error);
      setEmployeeData([]);
      setAddTableData([]);
    }
  };

  const handleGo = async () => {
    if (!customer || !project || !month || !year) {
      alert("Please select all filters ");
      return;
    }

    try {
      const response = await axiosInstance.get("/GetNewTimesheetDetails", {
        params: {
          projectId: project,
          customerId: customer,
          monthId: month,
          yearId: year,
        },
      });

      const detailArray = response?.data?.data;
      if (
        Array.isArray(detailArray) &&
        detailArray.length === 1 &&
        detailArray[0]?.message === "Timesheet already exists."
      ) {
        setAddTableData([]); // clear grid 
        setSnackbarMsg(detailArray[0].message); // ✅ set the message
        setSnackbarOpen(true); // ✅ open snackbar
        return;
      }




      const formattedData = detailArray.map((item: any) => ({
        employeeId: item.employeeId,
        employeeCode: item.employeeCode ?? "",
        firstName: item.firstName,
        lastName: item.lastName,
        bookedHours: item.bookedHours,
      }));

      setAddTableData(formattedData);


    } catch (error) {
      console.error("Error fetching timesheet data:", error);
      alert("Failed to fetch timesheet data");
    }
  };
  const [confirmOpen, setConfirmOpen] = useState(false);


  // 🔁 Replace this in your component
  const handlePreSave = () => {
    if (!customer || !project || !month || !year) {
      alert("Please select all filters before saving.");
      return;
    }
    setConfirmOpen(true); //  Only open confirmation dialog here
  };

  const handleSave = async () => {
    setConfirmOpen(false); // Close confirmation dialog immediately

    const timesheetDetailModel = addTableData.map((item) => ({
      employeeId: item.employeeId,
      employeeCode: item.employeeCode,
      firstName: item.firstName,
      lastName: item.lastName,
      bookedHours: item.bookedHours,
    }));

    const payload = {
      projectId: project,
      customerId: customer,
      monthId: month,
      yearId: year,
      timesheetDetailModel,
    };

    try {
      const response = await CreateTimesheet(payload);

      if (response.status === 200 || response.status === 201) {
        setShowSuccessSnackbar(true); //  Show only after success
        setTimeout(() => {
          navigate('/timesheet'); // Replace with your list page path
        }, 1000);
      } else {
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      setShowErrorSnackbar(true);
    }
  };
  function handleEdit(): void {
    throw new Error("Function not implemented.");
  }

  const handleBack = () => {
    navigate('/timesheet'); // Replace with your actual list page route
  };
  return (
    <Box sx={{ padding: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Timeline sx={{ color: "#1976d2" }} />
        <Typography variant="h5" fontWeight="bold">
          Add Timesheet
        </Typography>
      </Box>

      <Box display="flex" gap={4} alignItems="flex-start">

        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          width="100%"
          maxWidth="300px"
          sx={{
            backgroundColor: "#fff",
            padding: 3,
            borderRadius: 3,
            boxShadow: 1, // optional for elevation like SearchDrawer
          }}
        >
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="customer-label">Customer</InputLabel>
            <Select
              labelId="customer-label"
              id="customer-select"
              value={customer}
              label="Customer"
              onChange={async (e) => {
                const selectedCustomerId = Number(e.target.value);
                setCustomer(selectedCustomerId);
                setProject("");
                setProjects([]);
                try {
                  const response = await GetProjectByCustomerId(selectedCustomerId);
                  const customerProjects = Array.isArray(response.data?.data) ? response.data.data : [];
                  setProjects(customerProjects);
                } catch (error) {
                  setProjects([]);
                }
              }}
              disabled={!!id}
            >
              {customers.map((c) => (
                <MenuItem key={c.customerId} value={c.customerId}>
                  {c.customerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="project-label">Project</InputLabel>
            <Select
              labelId="project-label"
              id="project-select"
              value={project}
              label="Project"
              onChange={(e) => setProject(Number(e.target.value))}
              disabled={!!id}
            >
              {projects.length === 0 ? (
                <MenuItem disabled>No projects available</MenuItem>
              ) : (
                projects.map((p) => (
                  <MenuItem key={p.projectId} value={p.projectId}>
                    {p.projectName}
                  </MenuItem>
                ))
              )}
            </Select>
            {/* Optional helper text, add if needed */}
            {/* <FormHelperText>Select a project</FormHelperText> */}
          </FormControl>


          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="month-label">Month</InputLabel>
            <Select
              labelId="month-label"
              id="month-select"
              value={month}
              label="Month"
              onChange={(e) => setMonth(e.target.value)}
              disabled={Boolean(timesheetId)}
            >
              {months.map((m) => (
                <MenuItem key={m.tableID} value={m.tableID}>
                  {m.tableName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              labelId="year-label"
              id="year-select"
              value={year}
              label="Year"
              onChange={(e) => setYear(e.target.value)}
              disabled={Boolean(timesheetId)}
            >
              {years.map((y) => (
                <MenuItem key={y.tableID} value={y.tableID}>
                  {y.tableValueLong}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          <Button variant="contained" onClick={handleGo}>
            Go
          </Button>

          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>

        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="error" // or "success", "info", "warning"
            variant="filled"
          >
            {snackbarMsg}
          </Alert>
        </Snackbar>


        <ConfirmationDialog
          open={confirmOpen}
          title="Confirm Save"
          content="Are you sure you want to save the timesheet?"
          onConfirm={handleSave}
          onCancel={() => setConfirmOpen(false)}
        />

        <Snackbar
          open={showSuccessSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSuccessSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success" variant="filled">
            Timesheet saved successfully!
          </Alert>
        </Snackbar>

        <Snackbar
          open={showErrorSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowErrorSnackbar(false)}
        >
          <Alert onClose={() => setShowErrorSnackbar(false)} severity="error">
            Failed to save timesheet!
          </Alert>
        </Snackbar>



        <Box flex={1}>
          <div
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
              } else {
                e.stopPropagation();
              }
            }}
          >
            <AgGridTable
              domLayout="autoHeight"
              customStyles="legacy"
              columns={editColumns}
              data={addTableData}
              pagination={false}
              rowSelection="multiple"
              paginationPerPage={10}
              noDataComponentText="No data loaded"

              gridOptions={{
                defaultColDef: {
                  editable: true,
                  resizable: true,
                  flex: 1,
                },
                stopEditingWhenCellsLoseFocus: false,
                onGridReady: (params: any) => {
                  gridApi.current = params.api;

                  // Wait until rows are rendered before focusing
                  setTimeout(() => {
                    if (gridApi.current && addTableData.length > 0) {
                      gridApi.current.ensureIndexVisible(0); // optional, in case scroll is needed
                      gridApi.current.setFocusedCell(0, "bookedHours");
                      gridApi.current.startEditingCell({
                        rowIndex: 0,
                        colKey: "bookedHours",
                      });
                    }
                  }, 100); // use a small delay (100ms) to ensure rows are rendered
                },




                // ENTER or DOWN ARROW key
                onCellKeyDown: (params: any) => {
                  const colId = params.column.getColId();
                  const currentRowIndex = params.node.rowIndex;

                  if (
                    (params.event?.key === "Enter" || params.event?.key === "ArrowDown") &&
                    colId === "bookedHours"
                  ) {
                    params.event.preventDefault();
                    params.event.stopPropagation();

                    const nextRowIndex = currentRowIndex + 1;
                    const nextRowNode = params.api.getDisplayedRowAtIndex(nextRowIndex);

                    if (nextRowNode) {
                      setTimeout(() => {
                        params.api.setFocusedCell(nextRowIndex, "bookedHours");
                        params.api.startEditingCell({
                          rowIndex: nextRowIndex,
                          colKey: "bookedHours",
                        });
                      }, 0);
                    }
                  }
                },


                // Row click will make that row's bookedHours editable
                onRowClicked: (params: any) => {
                  setTimeout(() => {
                    params.api.setFocusedCell(params.rowIndex, "bookedHours");
                    params.api.startEditingCell({
                      rowIndex: params.rowIndex,
                      colKey: "bookedHours",
                    });
                  }, 0);
                },

                // Updating bookedHours
                onCellValueChanged: (params: any) => {
                  const newVal = params.newValue ?? 0;

                  const updatedData = addTableData.map((row) =>
                    row.employeeCode === params.data.employeeCode
                      ? {
                        ...row,
                        bookedHours:
                          newVal === "" || newVal === null || newVal === undefined
                            ? 0
                            : Number(newVal),
                      }
                      : row
                  );


                },
              }}
              handleSelectedRows={() => { }}
            />

          </div>

        </Box>

      </Box>

      <StickyFooterButtons
        isEditMode={false}
        isSaveMode={true}
        onSaveClick={handlePreSave}
        onEditClick={handleEdit}
        onBackClick={handleBack}
      />


    </Box>
  );
};

export default AddTimesheet;